import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const GeneralTsoChicken: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>General Tso's Chicken Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make restaurant-style General Tso's Chicken at home. Master the perfect balance of sweet, savory, and spicy flavors with our detailed recipe." />
        <meta property="og:title" content="General Tso's Chicken Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make restaurant-style General Tso's Chicken at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/general-tso-chicken" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/general-tso-chicken" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">General Tso's Chicken Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              General Tso's Chicken is a popular Chinese-American dish known for its perfect balance of sweet, savory, and spicy flavors. This recipe will help you create this takeout favorite at home.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Chicken:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 lbs chicken thighs, cut into 1-inch pieces</li>
              <li>1 cup cornstarch</li>
              <li>2 eggs</li>
              <li>1 tsp salt</li>
              <li>½ tsp white pepper</li>
              <li>Oil for deep frying</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ cup chicken stock</li>
              <li>¼ cup soy sauce</li>
              <li>¼ cup rice vinegar</li>
              <li>¼ cup Chinese cooking wine</li>
              <li>¼ cup hoisin sauce</li>
              <li>¼ cup brown sugar</li>
              <li>2 tbsp cornstarch</li>
              <li>6-8 dried red chilies</li>
              <li>4 cloves garlic, minced</li>
              <li>1-inch ginger, minced</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For Garnish:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 green onions, chopped</li>
              <li>Sesame seeds</li>
              <li>Steamed broccoli</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Prepare Chicken:</strong> Coat chicken in egg, then cornstarch mixture.
              </li>
              <li>
                <strong>First Fry:</strong> Deep fry chicken at 350°F until light golden.
              </li>
              <li>
                <strong>Make Sauce:</strong> Combine sauce ingredients except chilies, garlic, and ginger.
              </li>
              <li>
                <strong>Second Fry:</strong> Fry chicken again until deep golden and crispy.
              </li>
              <li>
                <strong>Finish:</strong> Stir-fry aromatics, add sauce, toss with chicken.
              </li>
              <li>
                <strong>Serve:</strong> Garnish with green onions and sesame seeds.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips from Chinese Food Maps:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Double-frying ensures extra crispy chicken</li>
                <li>Use chicken thighs for more flavor and moisture</li>
                <li>Adjust chili amount for desired heat level</li>
                <li>Serve immediately while chicken is crispy</li>
                <li>Keep sauce warm but not boiling</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};